import 'mapbox-gl/dist/mapbox-gl.css'
import './MapTiles.css'
import { memo, useRef, useEffect, useCallback, Component, type ReactNode } from 'react'
import mapboxgl, { type GeoJSONSource, type LngLatBoundsLike } from 'mapbox-gl';
import { Box, Typography } from '@mui/material';
import { SAME_POINT_PRECISION } from './mapCurves';
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const MAPBOX_STYLE = 'mapbox://styles/mapbox/standard';

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function sanitizeCenter(center?: [number, number]): [number, number] | undefined {
  if (!center) return undefined;
  const lng = toFiniteNumber(center[0]);
  const lat = toFiniteNumber(center[1]);
  if (lng === null || lat === null) return undefined;
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return undefined;
  return [lng, lat];
}

function sanitizeZoom(zoom?: number): number | undefined {
  const z = toFiniteNumber(zoom);
  return z === null ? undefined : z;
}

type ArrayBounds = [number, number, number, number] | [[number, number], [number, number]];

function sanitizeBounds(bounds?: LngLatBoundsLike): ArrayBounds | undefined {
  if (!bounds) return undefined;

  if (Array.isArray(bounds) && bounds.length === 4) {
    const minLng = toFiniteNumber(bounds[0]);
    const minLat = toFiniteNumber(bounds[1]);
    const maxLng = toFiniteNumber(bounds[2]);
    const maxLat = toFiniteNumber(bounds[3]);
    if (minLng === null || minLat === null || maxLng === null || maxLat === null) return undefined;
    if (minLng > maxLng || minLat > maxLat) return undefined;
    return [minLng, minLat, maxLng, maxLat];
  }

  if (Array.isArray(bounds) && bounds.length === 2) {
    const sw = bounds[0];
    const ne = bounds[1];
    if (!Array.isArray(sw) || !Array.isArray(ne) || sw.length < 2 || ne.length < 2) return undefined;
    const swLng = toFiniteNumber(sw[0]);
    const swLat = toFiniteNumber(sw[1]);
    const neLng = toFiniteNumber(ne[0]);
    const neLat = toFiniteNumber(ne[1]);
    if (swLng === null || swLat === null || neLng === null || neLat === null) return undefined;
    if (swLng > neLng || swLat > neLat) return undefined;
    return [[swLng, swLat], [neLng, neLat]];
  }

  return undefined;
}

const MIN_BOUNDS_SPAN_KM = 2;
const KM_PER_LAT_DEGREE = 111;

// A single-event bbox is a point (zero area). Left as-is, fitBounds zooms in
// all the way to street level, which shows an unrecognizable patch of asphalt
// instead of giving the user any sense of where the place is. Padding the
// bbox out to a minimum span keeps a bit of surrounding context in view.
function isPairBounds(bounds: ArrayBounds): bounds is [[number, number], [number, number]] {
  return Array.isArray(bounds[0]);
}

function ensureMinimumBoundsSpan(bounds: ArrayBounds, minSpanKm = MIN_BOUNDS_SPAN_KM): [number, number, number, number] {
  const [minLng, minLat, maxLng, maxLat]: [number, number, number, number] = isPairBounds(bounds)
    ? [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]
    : bounds;

  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  const halfSpanKm = minSpanKm / 2;
  const minHalfLatDeg = halfSpanKm / KM_PER_LAT_DEGREE;
  const kmPerLngDegree = KM_PER_LAT_DEGREE * Math.cos(centerLat * Math.PI / 180);
  const minHalfLngDeg = halfSpanKm / (kmPerLngDegree || KM_PER_LAT_DEGREE);
  const halfLatDeg = Math.max((maxLat - minLat) / 2, minHalfLatDeg);
  const halfLngDeg = Math.max((maxLng - minLng) / 2, minHalfLngDeg);

  return [centerLng - halfLngDeg, centerLat - halfLatDeg, centerLng + halfLngDeg, centerLat + halfLatDeg];
}

// Mapbox's `text-offset` places every label at the same fixed anchor
// regardless of what else is on the map -- it does not know or care that two
// Point features share a coordinate, so multiple distinct titles (different
// people, or different events for the same person, resolving to the same
// village/church) render fully superimposed. There is no built-in Mapbox
// option for this ("allow-overlap" only controls whether colliding labels
// hide each other, it doesn't relocate them); the fix is to precompute a
// per-feature vertical offset for anything sharing a coordinate with another
// point, so the stack reads as a list instead of a smear of overlapping text.
const LABEL_STACK_BASE_OFFSET_EM = 1.2; // matches the single-label default below
const LABEL_STACK_STEP_EM = 1.4; // vertical gap between stacked labels

function pointGroupKey(feature: GeoJSON.Feature): string | null {
  if (feature.geometry.type !== 'Point') return null;
  const [lng, lat] = feature.geometry.coordinates;
  return `${lng.toFixed(SAME_POINT_PRECISION)},${lat.toFixed(SAME_POINT_PRECISION)}`;
}

function withStackedLabelOffsets(features: GeoJSON.Feature[]): GeoJSON.Feature[] {
  const groups = new Map<string, GeoJSON.Feature[]>();
  features.forEach((feature) => {
    const key = pointGroupKey(feature);
    if (!key) return;
    const group = groups.get(key) ?? [];
    group.push(feature);
    groups.set(key, group);
  });

  return features.map((feature) => {
    const key = pointGroupKey(feature);
    const group = key ? groups.get(key) : undefined;
    if (!group || group.length < 2) return feature;
    const stackIndex = group.indexOf(feature);
    return {
      ...feature,
      properties: {
        ...feature.properties,
        textOffset: [0, LABEL_STACK_BASE_OFFSET_EM + stackIndex * LABEL_STACK_STEP_EM],
      },
    };
  });
}

function MapTilesFallback() {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa', p: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Le rendu de la carte n'est pas disponible sur ce navigateur ou cette machine (WebGL indisponible).
      </Typography>
    </Box>
  )
}

class ErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Mapbox initialization failed', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

export const MapTiles = memo(function MapTiles({features, bounds, center, zoom, onLoaded, markerLabelColor = '#2f1b1b'}: {features: GeoJSON.Feature[], bounds?: LngLatBoundsLike, center?: [number, number] | undefined, zoom?: number, onLoaded?: (map: mapboxgl.Map) => void, markerLabelColor?: string}) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map>(null);
  const updateGeoJSON = useCallback((features: GeoJSON.Feature[]) => {
    if (!map.current) return;

    const geo: GeoJSONSource = map.current!.getSource('dynamic_markers')!
    if (!geo) return;

    geo.setData({
      type: 'FeatureCollection',
      features: withStackedLabelOffsets(features)
    });
  }, []);

  useEffect(() => {
    updateGeoJSON(features);
  }, [map, features, updateGeoJSON]);

  // flyTo (not setCenter/setZoom) so that re-centering on a newly focused place
  // reads as the map traveling there rather than jumping instantly.
  useEffect(() => {
    const safeCenter = sanitizeCenter(center);
    const safeZoom = sanitizeZoom(zoom);
    const mapInstance = map.current;
    if (!mapInstance) return;
    const currentCenter = mapInstance.getCenter();
    const centerChanged = safeCenter && (
      Math.abs(currentCenter.lng - safeCenter[0]) > 1e-8 ||
      Math.abs(currentCenter.lat - safeCenter[1]) > 1e-8
    );
    const zoomChanged = safeZoom !== undefined && Math.abs(mapInstance.getZoom() - safeZoom) > 1e-8;
    if (!centerChanged && !zoomChanged) return;
    mapInstance.flyTo({
      ...(safeCenter ? { center: safeCenter } : {}),
      ...(safeZoom !== undefined ? { zoom: safeZoom } : {}),
      essential: true,
    });
  }, [center, zoom, map]);

  useEffect(() => {
    // console.log('bounds changed', bounds)
    const safeBounds = sanitizeBounds(bounds);
    if (safeBounds) map.current?.fitBounds(ensureMinimumBoundsSpan(safeBounds), { padding: 20 });
  }, [bounds, map]);

  useEffect(() => {
    if (map.current) return; // initialize map only once
    if (!MAPBOX_TOKEN) {
      throw new Error('Missing MAPBOX_TOKEN: set it in your env to render the Mapbox map.');
    }

    // The 'load' event below is async (network-dependent). If this component
    // unmounts before it fires, the cleanup already called map.remove() and
    // nulled map.current -- without this guard the stale handler would still
    // run and touch the torn-down map/container, which throws a DOM
    // insertBefore error from addControl.
    let cancelled = false;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const safeCenter = sanitizeCenter(center);
    const safeZoom = sanitizeZoom(zoom);
    const safeBounds = sanitizeBounds(bounds);
    const initialCenter: [number, number] = safeCenter ?? [2.2137, 46.2276];
    const initialZoom = safeZoom ?? 4;

    map.current = new mapboxgl.Map({
      container: mapContainer.current!,
      zoom: initialZoom,
      center: initialCenter,
      boxZoom: true,
      attributionControl: false,
      performanceMetricsCollection: false,
      style: MAPBOX_STYLE,
    });

    map.current.on("load", (e) => {
      if (cancelled) return;

      e.target.addControl(new mapboxgl.ScaleControl({
        maxWidth: 200,
        unit: 'metric'
      }), 'top-left');

      if (safeBounds) {
        e.target.fitBounds(ensureMinimumBoundsSpan(safeBounds), { padding: 20 });
      }

      e.target.addSource('dynamic_markers', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: withStackedLabelOffsets(features) },
        lineMetrics: true
      });

      e.target.addLayer({
        id: 'linestring-layer',
        type: 'line',
        source: 'dynamic_markers',
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#92400e',
          'line-width': 2.75,
          // Pale where people left, dark where they went (matching the atelier atlas).
          'line-gradient': [
            'interpolate',
            ['linear'],
            ['line-progress'],
            0, 'rgba(217, 119, 6, 0.30)',
            0.5, 'rgba(180, 83, 9, 0.68)',
            1, 'rgba(120, 53, 15, 0.96)',
          ]
        }
      });

      e.target.addLayer({
        id: 'markers',
        type: 'circle',
        source: 'dynamic_markers',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#b45309',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff'
        }
      });

      e.target.addLayer({
        id: 'markers-label',
        type: 'symbol',
        source: 'dynamic_markers',
        filter: ['==', ['geometry-type'], 'Point'],
        layout: {
          'text-field': ['coalesce', ['get', 'title'], ''],
          'text-offset': ['coalesce', ['get', 'textOffset'], ['literal', [0, 1.2]]],
          'text-size': 12,
          'text-anchor': 'top',
          'text-allow-overlap': true
        },
        paint: {
          'text-color': markerLabelColor,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2
        }
      });

      if (onLoaded) {
        onLoaded(map.current!)
      }

      // Signals that the linestring/marker features under the pointer are clickable.
      const canvas = e.target.getCanvas();
      for (const layerId of ['linestring-layer', 'markers']) {
        e.target.on('mouseenter', layerId, () => { canvas.style.cursor = 'pointer'; });
        e.target.on('mouseleave', layerId, () => { canvas.style.cursor = ''; });
      }
    })

    return () => {
      cancelled = true;
      map.current?.remove()
      map.current = null
    }
    // Deliberately not reactive to features/bounds/center/zoom/onLoaded: this effect
    // creates and destroys the Mapbox instance, and re-running it on every data change
    // would tear the map down and rebuild it. Those inputs are applied by the dedicated
    // effects above; onLoaded must be stable in the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerLabelColor]);

  useEffect(() => {
    const container = mapContainer.current;
    if (!container) {
      return;
    }

    const resizeMap = () => {
      map.current?.resize();
    };

    const observer = new ResizeObserver(() => {
      resizeMap();
    });

    observer.observe(container);
    window.addEventListener('resize', resizeMap);
    resizeMap();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resizeMap);
    };
  }, []);


  return (
    <ErrorBoundary fallback={<MapTilesFallback />}>
      <Box id="map" className="map-container" ref={mapContainer} sx={{ height: '100%' }}>
      </Box>
    </ErrorBoundary>
  )
})
